import type { DesktopSpec, DeviceFinish, DeviceModel, LaptopSpec, SlateSpec } from "./catalog";

/**
 * Flat drawings of the device parts (docs/19-mockups.md). A face is a small
 * vector drawing in millimetres (browser windows: window pixels) with a hole
 * for the screen: `shapes` lie below the screenshot, `overlay` on top of it
 * (Dynamic Island, notch, camera). The renderer turns them into SVG; the 3D
 * placement of the faces is `layout.ts`.
 */

/** Linear gradient with CSS angle semantics (0 = bottom → top, 90 = left → right). */
export interface DeviceGradient {
  angle: number;
  stops: [offset: number, color: string][];
}

export type DevicePaint = string | DeviceGradient;

export interface DeviceStroke {
  color: string;
  width: number;
}

export type DeviceShape =
  | {
      kind: "rect";
      x: number;
      y: number;
      width: number;
      height: number;
      radius?: DeviceRadius;
      fill: DevicePaint;
      stroke?: DeviceStroke;
    }
  | {
      kind: "circle";
      cx: number;
      cy: number;
      r: number;
      fill: DevicePaint;
      stroke?: DeviceStroke;
    }
  | { kind: "path"; d: string; fill: DevicePaint | "none"; stroke?: DeviceStroke }
  | {
      kind: "text";
      x: number;
      y: number;
      size: number;
      text: string;
      fill: string;
      weight?: number;
    };

/** Corner radii: one for all or top-left, top-right, bottom-right, bottom-left. */
export type DeviceRadius = number | [number, number, number, number];

export interface DeviceScreen {
  x: number;
  y: number;
  width: number;
  height: number;
  radius: DeviceRadius;
  /** Browser pages start at the top when they are cropped. */
  align: "center" | "top";
}

export interface DeviceFace {
  /** Size of the face as drawn (after turning it to landscape). */
  width: number;
  height: number;
  /** Drawn in portrait and turned by -90° (landscape phones and tablets). */
  rotated: boolean;
  shapes: DeviceShape[];
  overlay: DeviceShape[];
  /** Where the screenshot goes, in the face's final coordinates. */
  screen: DeviceScreen | null;
}

// ---------------------------------------------------------------------------
// Colour helpers
// ---------------------------------------------------------------------------

function channels(hex: string): [number, number, number] {
  const value = hex.replace("#", "");
  const full =
    value.length === 3
      ? value
          .split("")
          .map((char) => char + char)
          .join("")
      : value.slice(0, 6);
  return [0, 2, 4].map((index) => Number.parseInt(full.slice(index, index + 2), 16)) as [
    number,
    number,
    number,
  ];
}

/** Lighter (amount > 0, towards white) or darker (amount < 0, towards black). */
export function shade(hex: string, amount: number): string {
  const target = amount > 0 ? 255 : 0;
  const t = Math.min(1, Math.abs(amount));
  return `#${channels(hex)
    .map((channel) =>
      Math.round(channel + (target - channel) * t)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;
}

/** Brushed metal: lighter along one edge, darker along the other. */
function metal(color: string, angle = 160): DeviceGradient {
  return {
    angle,
    stops: [
      [0, shade(color, 0.18)],
      [0.45, color],
      [1, shade(color, -0.14)],
    ],
  };
}

// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------

const f = (value: number) => Number(value.toFixed(3));

/** Rounded rectangle as path data (per-corner radii). */
export function roundedRectPath(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: DeviceRadius,
): string {
  const max = Math.min(width, height) / 2;
  const [tl, tr, br, bl] = (
    typeof radius === "number" ? [radius, radius, radius, radius] : radius
  ).map((value) => Math.max(0, Math.min(max, value))) as [number, number, number, number];
  return [
    `M${f(x + tl)} ${f(y)}`,
    `H${f(x + width - tr)}`,
    tr ? `A${f(tr)} ${f(tr)} 0 0 1 ${f(x + width)} ${f(y + tr)}` : "",
    `V${f(y + height - br)}`,
    br ? `A${f(br)} ${f(br)} 0 0 1 ${f(x + width - br)} ${f(y + height)}` : "",
    `H${f(x + bl)}`,
    bl ? `A${f(bl)} ${f(bl)} 0 0 1 ${f(x)} ${f(y + height - bl)}` : "",
    `V${f(y + tl)}`,
    tl ? `A${f(tl)} ${f(tl)} 0 0 1 ${f(x + tl)} ${f(y)}` : "",
    "Z",
  ]
    .filter(Boolean)
    .join("");
}

/** Notch hanging from the top edge of the screen, with soft shoulders. */
function notchPath(centerX: number, top: number, width: number, height: number): string {
  const half = width / 2;
  const ear = Math.min(1.2, height * 0.25);
  const r = Math.min(height * 0.55, 3);
  const left = centerX - half;
  const right = centerX + half;
  return [
    `M${f(left - ear)} ${f(top - 0.5)}`,
    `V${f(top)}`,
    `Q${f(left)} ${f(top)} ${f(left)} ${f(top + ear)}`,
    `V${f(top + height - r)}`,
    `Q${f(left)} ${f(top + height)} ${f(left + r)} ${f(top + height)}`,
    `H${f(right - r)}`,
    `Q${f(right)} ${f(top + height)} ${f(right)} ${f(top + height - r)}`,
    `V${f(top + ear)}`,
    `Q${f(right)} ${f(top)} ${f(right + ear)} ${f(top)}`,
    `V${f(top - 0.5)}`,
    "Z",
  ].join("");
}

// ---------------------------------------------------------------------------
// Screens
// ---------------------------------------------------------------------------

const GLASS = "#0b0b0d";
const INK = "#050506";

/** Screen size from the diagonal (inches) and the resolution's aspect (height / width). */
function screenSize(diagonal: number, aspect: number): { width: number; height: number } {
  const width = (diagonal * 25.4) / Math.sqrt(1 + aspect * aspect);
  return { width, height: width * aspect };
}

// ---------------------------------------------------------------------------
// Phones and tablets
// ---------------------------------------------------------------------------

export function slateScreen(model: DeviceModel, spec: SlateSpec) {
  const size = screenSize(spec.diagonal, model.screen.height / model.screen.width);
  const x = (spec.width - size.width) / 2;
  const y = (spec.height - size.height) / 2;
  const bezel = Math.min(x, y);
  const radius = spec.screenRadius ?? Math.max(0, spec.radius - bezel);
  return { x, y, ...size, radius };
}

function slateFace(
  model: DeviceModel,
  spec: SlateSpec,
  finish: DeviceFinish,
  landscape: boolean,
): DeviceFace {
  const { width, height, radius, rim } = spec;
  const screen = slateScreen(model, spec);
  const shapes: DeviceShape[] = [];
  const button = shade(finish.side, -0.05);
  for (const entry of spec.buttons ?? []) {
    if (entry.side === "top") {
      shapes.push({
        kind: "rect",
        x: entry.from,
        y: -0.55,
        width: entry.length,
        height: 1.4,
        radius: 0.5,
        fill: button,
      });
    } else {
      shapes.push({
        kind: "rect",
        x: entry.side === "left" ? -0.55 : width - 0.85,
        y: entry.from,
        width: 1.4,
        height: entry.length,
        radius: 0.5,
        fill: button,
      });
    }
  }
  shapes.push(
    { kind: "rect", x: 0, y: 0, width, height, radius, fill: metal(finish.frame) },
    {
      kind: "rect",
      x: rim,
      y: rim,
      width: width - rim * 2,
      height: height - rim * 2,
      radius: Math.max(0, radius - rim),
      fill: GLASS,
    },
  );
  const overlay: DeviceShape[] = [];
  const centerX = width / 2;
  const cutout = spec.cutout;
  if (cutout?.type === "island") {
    overlay.push({
      kind: "rect",
      x: centerX - cutout.width / 2,
      y: screen.y + cutout.top,
      width: cutout.width,
      height: cutout.height,
      radius: cutout.height / 2,
      fill: INK,
    });
  } else if (cutout?.type === "notch") {
    overlay.push({
      kind: "path",
      d: notchPath(centerX, screen.y, cutout.width, cutout.height),
      fill: GLASS,
    });
  } else if (cutout?.type === "hole") {
    overlay.push({
      kind: "circle",
      cx: centerX,
      cy: screen.y + cutout.top + cutout.size / 2,
      r: cutout.size / 2,
      fill: INK,
      stroke: { color: "#1c1d21", width: 0.25 },
    });
  }
  if (spec.homeButton) {
    const size = spec.homeButton;
    const bottom = screen.y + screen.height;
    overlay.push(
      {
        kind: "circle",
        cx: centerX,
        cy: bottom + (height - bottom) / 2,
        r: size / 2,
        fill: "#141518",
        stroke: { color: shade(finish.frame, -0.1), width: 0.45 },
      },
      {
        kind: "rect",
        x: centerX - 5.2,
        y: screen.y / 2 - 0.65,
        width: 10.4,
        height: 1.3,
        radius: 0.65,
        fill: "#1d1e22",
      },
      { kind: "circle", cx: centerX - 11.5, cy: screen.y / 2, r: 1.25, fill: "#17181c" },
    );
  }
  if (spec.camera) {
    overlay.push({ kind: "circle", cx: centerX, cy: screen.y / 2, r: 1.15, fill: "#16171b" });
  }
  const portraitScreen: DeviceScreen = {
    x: screen.x,
    y: screen.y,
    width: screen.width,
    height: screen.height,
    radius: screen.radius,
    align: "center",
  };
  if (!landscape) {
    return { width, height, rotated: false, shapes, overlay, screen: portraitScreen };
  }
  // Turned to the left: the top (camera) ends up on the left side.
  return {
    width: height,
    height: width,
    rotated: true,
    shapes,
    overlay,
    screen: {
      ...portraitScreen,
      x: screen.y,
      y: width - screen.x - screen.width,
      width: screen.height,
      height: screen.width,
    },
  };
}

// ---------------------------------------------------------------------------
// Laptops
// ---------------------------------------------------------------------------

export function laptopScreen(model: DeviceModel, spec: LaptopSpec) {
  const size = screenSize(spec.diagonal, model.screen.height / model.screen.width);
  return { x: (spec.width - size.width) / 2, y: spec.bezelTop, ...size };
}

function laptopLid(model: DeviceModel, spec: LaptopSpec, finish: DeviceFinish): DeviceFace {
  const width = spec.width;
  const height = spec.lidHeight;
  const screen = laptopScreen(model, spec);
  const rim = 1.6;
  const corner = Math.min(spec.radius, 8);
  const shapes: DeviceShape[] = [
    { kind: "rect", x: 0, y: 0, width, height, radius: corner, fill: metal(finish.frame, 170) },
    {
      kind: "rect",
      x: rim,
      y: rim,
      width: width - rim * 2,
      height: height - rim * 2 - 3.5,
      radius: [corner - rim, corner - rim, 2, 2],
      fill: GLASS,
    },
    // Hinge cover along the bottom edge.
    {
      kind: "rect",
      x: rim + 12,
      y: height - rim - 3.5,
      width: width - rim * 2 - 24,
      height: 3.5,
      fill: shade(finish.frame, -0.28),
    },
  ];
  const overlay: DeviceShape[] = [];
  if (spec.notch) {
    const { width: notchWidth, height: notchHeight } = spec.notch;
    const left = width / 2 - notchWidth / 2;
    overlay.push(
      {
        kind: "path",
        d: roundedRectPath(left, screen.y - 1, notchWidth, notchHeight + 1, [0, 0, 2.2, 2.2]),
        fill: GLASS,
      },
      { kind: "circle", cx: width / 2, cy: screen.y + notchHeight * 0.45, r: 1, fill: "#1b1c21" },
    );
  } else if (spec.camera) {
    overlay.push({ kind: "circle", cx: width / 2, cy: screen.y / 2, r: 1.1, fill: "#1b1c21" });
  }
  return {
    width,
    height,
    rotated: false,
    shapes,
    overlay,
    screen: { ...screen, radius: spec.notch ? [3.5, 3.5, 0, 0] : 0, align: "center" },
  };
}

/** Keyboard deck of the base, drawn from the hinge (top) to the front edge (bottom). */
function laptopDeck(spec: LaptopSpec, finish: DeviceFinish): DeviceFace {
  const width = spec.width;
  const height = spec.depth;
  const deck = finish.frame;
  const shapes: DeviceShape[] = [
    {
      kind: "rect",
      x: 0,
      y: 0,
      width,
      height,
      radius: spec.radius,
      fill: {
        angle: 180,
        stops: [
          [0, shade(deck, -0.06)],
          [1, shade(deck, 0.08)],
        ],
      },
    },
  ];
  const keyboard = {
    x: width * 0.075,
    y: height * 0.06,
    width: width * 0.85,
    height: height * 0.4,
  };
  shapes.push({
    kind: "rect",
    ...keyboard,
    radius: 2.5,
    fill: shade(deck, -0.1),
  });
  const keyColor = "#1c1c1f";
  const gap = keyboard.width * 0.006;
  const rows = 6;
  const rowHeight = (keyboard.height - gap * (rows + 1)) / rows;
  for (let row = 0; row < rows; row++) {
    const y = keyboard.y + gap + row * (rowHeight + gap);
    // Units per row (the last row has the space bar in the middle).
    const units =
      row === rows - 1 ? [1, 1, 1, 1.3, 5.2, 1.3, 1, 1, 1] : Array.from({ length: 14 }, () => 1);
    const total = units.reduce((sum, unit) => sum + unit, 0);
    const unitWidth = (keyboard.width - gap * (units.length + 1)) / total;
    let x = keyboard.x + gap;
    for (const unit of units) {
      const keyWidth = unit * unitWidth + (unit - 1) * gap;
      shapes.push({
        kind: "rect",
        x,
        y,
        width: keyWidth,
        height: row === 0 ? rowHeight * 0.7 : rowHeight,
        radius: 0.9,
        fill: keyColor,
      });
      x += keyWidth + gap;
    }
  }
  const pad = { width: width * 0.42, height: height * 0.34 };
  shapes.push({
    kind: "rect",
    x: (width - pad.width) / 2,
    y: height * 0.52,
    ...pad,
    radius: 4,
    fill: shade(deck, -0.035),
    stroke: { color: shade(deck, -0.14), width: 0.35 },
  });
  return { width, height, rotated: false, shapes, overlay: [], screen: null };
}

// ---------------------------------------------------------------------------
// Desktops
// ---------------------------------------------------------------------------

export function desktopScreen(model: DeviceModel, spec: DesktopSpec) {
  const size = screenSize(spec.diagonal, model.screen.height / model.screen.width);
  return { x: (spec.width - size.width) / 2, y: spec.bezelTop, ...size };
}

function desktopFace(model: DeviceModel, spec: DesktopSpec, finish: DeviceFinish): DeviceFace {
  const { width, height, radius } = spec;
  const screen = desktopScreen(model, spec);
  const side = screen.x;
  const glassHeight = screen.y + screen.height + side;
  const shapes: DeviceShape[] = spec.glass
    ? [
        // iMac: coloured chin below a light glass border.
        {
          kind: "rect",
          x: 0,
          y: 0,
          width,
          height,
          radius,
          fill: {
            angle: 180,
            stops: [
              [0, shade(finish.chin ?? finish.frame, -0.04)],
              [1, finish.chin ?? finish.frame],
            ],
          },
        },
        {
          kind: "path",
          d: roundedRectPath(0, 0, width, glassHeight, [radius, radius, 0, 0]),
          fill: spec.glass,
        },
        {
          kind: "rect",
          x: 0,
          y: glassHeight - 0.4,
          width,
          height: 0.8,
          fill: shade(spec.glass, -0.08),
        },
      ]
    : [
        { kind: "rect", x: 0, y: 0, width, height, radius, fill: metal(finish.frame, 170) },
        {
          kind: "rect",
          x: 1.2,
          y: 1.2,
          width: width - 2.4,
          height: height - 2.4,
          radius: Math.max(0, radius - 1.2),
          fill: GLASS,
        },
      ];
  const overlay: DeviceShape[] = spec.glass
    ? [{ kind: "circle", cx: width / 2, cy: screen.y / 2, r: 1.3, fill: "#2a2b30" }]
    : [];
  return {
    width,
    height,
    rotated: false,
    shapes,
    overlay,
    screen: { ...screen, radius: 0, align: "center" },
  };
}

/** Neck and foot of a desktop stand: plain metal plates. */
function standFace(width: number, height: number, color: string, radius: number): DeviceFace {
  return {
    width,
    height,
    rotated: false,
    shapes: [{ kind: "rect", x: 0, y: 0, width, height, radius, fill: metal(color, 90) }],
    overlay: [],
    screen: null,
  };
}

// ---------------------------------------------------------------------------
// Browser window
// ---------------------------------------------------------------------------

export const BROWSER_BAR = 76;
export const BROWSER_RADIUS = 14;

/** Page aspect (height / width) of a browser window, limited to sensible shapes. */
export function browserAspect(contentAspect: number | null | undefined): number {
  const aspect = contentAspect && Number.isFinite(contentAspect) ? contentAspect : 900 / 1440;
  return Math.min(3, Math.max(0.3, aspect));
}

function browserFace(
  width: number,
  finish: DeviceFinish,
  contentAspect: number | null | undefined,
  url: string | undefined,
): DeviceFace {
  const dark = finish.id === "dunkel";
  const pageHeight = Math.round(width * browserAspect(contentAspect));
  const height = BROWSER_BAR + pageHeight;
  const border = dark ? "#3f3f46" : "#d4d4d8";
  const muted = dark ? "#71717a" : "#a1a1aa";
  const shapes: DeviceShape[] = [
    {
      kind: "rect",
      x: 0,
      y: 0,
      width,
      height,
      radius: BROWSER_RADIUS,
      fill: finish.frame,
      stroke: { color: border, width: 1.5 },
    },
    { kind: "circle", cx: 30, cy: BROWSER_BAR / 2, r: 7, fill: "#ff5f57" },
    { kind: "circle", cx: 54, cy: BROWSER_BAR / 2, r: 7, fill: "#febc2e" },
    { kind: "circle", cx: 78, cy: BROWSER_BAR / 2, r: 7, fill: "#28c840" },
    {
      kind: "path",
      d: `M122 ${BROWSER_BAR / 2 - 8}L114 ${BROWSER_BAR / 2}L122 ${BROWSER_BAR / 2 + 8}`,
      fill: "none",
      stroke: { color: muted, width: 2.5 },
    },
    {
      kind: "path",
      d: `M146 ${BROWSER_BAR / 2 - 8}L154 ${BROWSER_BAR / 2}L146 ${BROWSER_BAR / 2 + 8}`,
      fill: "none",
      stroke: { color: shade(muted, dark ? -0.3 : 0.35), width: 2.5 },
    },
    {
      kind: "rect",
      x: width * 0.28,
      y: BROWSER_BAR / 2 - 18,
      width: width * 0.44,
      height: 36,
      radius: 10,
      fill: finish.side,
    },
  ];
  const address = url?.trim();
  if (address) {
    shapes.push({
      kind: "text",
      x: width / 2,
      y: BROWSER_BAR / 2 + 5.5,
      size: 16,
      text: address.length > 70 ? `${address.slice(0, 69)}…` : address,
      fill: dark ? "#d4d4d8" : "#52525b",
      weight: 500,
    });
  }
  return {
    width,
    height,
    rotated: false,
    shapes,
    overlay: [{ kind: "rect", x: 0, y: BROWSER_BAR - 1, width, height: 1, fill: border }],
    screen: {
      x: 0.75,
      y: BROWSER_BAR,
      width: width - 1.5,
      height: pageHeight - 0.75,
      radius: [0, 0, BROWSER_RADIUS - 1, BROWSER_RADIUS - 1],
      align: "top",
    },
  };
}

// ---------------------------------------------------------------------------

export interface DeviceFaces {
  main: DeviceFace;
  /** Laptops: the keyboard deck. */
  deck?: DeviceFace;
  /** Desktops: neck and foot of the stand. */
  neck?: DeviceFace;
  foot?: DeviceFace;
}

export function deviceFaces(
  model: DeviceModel,
  finish: DeviceFinish,
  options: { landscape: boolean; contentAspect?: number | null; url?: string },
): DeviceFaces {
  const spec = model.spec;
  switch (spec.kind) {
    case "slate":
      return { main: slateFace(model, spec, finish, options.landscape) };
    case "laptop":
      return { main: laptopLid(model, spec, finish), deck: laptopDeck(spec, finish) };
    case "desktop": {
      const color = spec.glass ? finish.frame : finish.side;
      const neckHeight = spec.height * 0.4 + spec.stand.height;
      return {
        main: desktopFace(model, spec, finish),
        neck: standFace(spec.stand.width, neckHeight, color, 2),
        foot: standFace(spec.stand.footWidth, spec.stand.footDepth, color, 10),
      };
    }
    case "browser":
      return { main: browserFace(spec.width, finish, options.contentAspect, options.url) };
  }
}
